import { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { SessionContext } from "../common/contexts/SessionContext";
import { useDebouncedValue } from "../common/helperfunctions/useDebouncedValue";
import { ColumnsSettings } from "../common/tables/ColumnsSelector/ColumnsSelector";
import { LucideIcon } from "../common/icon/LucideIcon";
import { getDatasetsZip } from "../Dataset/common/DownloadDataset/DownloadDataset";
import { showtoast } from "../common/overlays/Toasts/showtoast";
import TableView from "../common/panels/TableView/TableView";
import { SearchInput } from "../common/forms/SearchInput/SearchInput";
import { SharedBundlesFilterBar } from "./SharedBundlesFilterBar";
import { DateTimeRenderer } from "../common/datetime/DateTimeFormatter";
import { DatasetFilters, Dataset, datasetsConstants, DatasetFieldLabels } from "../api/Datasets";
import { useHistory, useParams } from "react-router-dom";
import { useIdsOnly, usePost } from "../api/BaseEntityApi";
import { SharedContentPublicModel, SharedContentPublicModelRequest } from "../api/SharedContent";
import { LoadingWrapper } from "../common/LoadingWrapper";
import { Alert } from "../common/overlays/Alert/Alert";
import { EntityTable } from "../common/tables/EntityTable/EntityTable";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { GetPersons, TableArrayRenderer } from "../common/misc/EntityRenders/EntityRenderer";
import { switchDatasetsDefaultSortState } from "../Dataset/Table/DatasetsTable";
import {
  getSortingFn,
  useGenericVirtualizedTable,
} from "../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { InferSortByOptions } from "../api/GenericTypes";

const columnSetting: ColumnsSettings<Dataset> = {
  "default-id": { pos: 0, active: false, header: "Dataset-ID", property: "id" },
  "default-name": { pos: 1, active: true, header: "Name", property: "name" },
  "default-creationDate": { pos: 3, active: true, header: "Creation date", property: "creationDate" },
  "default-enteredOn": { pos: 4, active: false, header: DatasetFieldLabels.enteredOn, property: "enteredOn" },
  "default-enteredBy": { pos: 5, active: false, header: DatasetFieldLabels.enteredBy, property: "enteredBy" },
  "default-modifiedOn": { pos: 6, active: false, header: DatasetFieldLabels.modifiedOn, property: "modifiedOn" },
  "default-modifiedBy": { pos: 7, active: false, header: DatasetFieldLabels.modifiedBy, property: "modifiedBy" },
  "default-projects": { pos: 8, active: true, header: "Projects", property: "projects" },
};

export const SharedBundlesView = () => {
  const history = useHistory();
  const { api, route } = useContext(SessionContext);

  const { selection, resultsCount, onCountChange, onSelectionChange } = useGenericVirtualizedTable<Dataset>();

  const [filters, setFilters] = useState<DatasetFilters>({
    sortBy: [
      {
        property: "CREATION_DATE",
        sortDirection: "DESC",
      },
    ],
  });
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebouncedValue(searchValue);
  const [sort, setSort] = useState<SortState<InferSortByOptions<DatasetFilters>>[]>(
    switchDatasetsDefaultSortState(filters.sortBy)
  );

  useEffect(() => {
    setFilters((prev) => ({ ...prev, searchTerm: debouncedSearchValue }));
  }, [debouncedSearchValue]);

  const columns = useMemo(() => {
    return [
      {
        id: "default-id",
        Header: "Dataset-ID",
        accessor: (row) => (
          <span style={{ color: "var(--gray-400)" }}>
            <samp>{row.id}</samp>
          </span>
        ),
        width: 135,
        minWidth: 135,
        align: "right",
        ...getSortingFn("ID", sort, setSort, setFilters),
      },
      {
        id: "default-name",
        Header: "Name",
        accessor: (row) => (
          <div style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden" }}>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                <LucideIcon name="activity" color={"var(--primary)"} /> {row.name}
              </span>
            </div>
            {!!row.isDeleted && (
              <div>
                <label className="label label-soft-warning">Trashed</label>
              </div>
            )}
          </div>
        ),
        minWidth: 150,
        width: 250,
        align: "left",
        ...getSortingFn("NAME", sort, setSort, setFilters),
      },
      {
        id: "default-creationDate",
        Header: "Creation date",
        accessor: (row) => <DateTimeRenderer date={row.creationDate} includeElapsed={false} />,
        width: 170,
        minWidth: 170,
        align: "left",
        ...getSortingFn("CREATION_DATE", sort, setSort, setFilters),
      },
      {
        id: "default-enteredOn",
        Header: "Entered on",
        accessor: (row) => <DateTimeRenderer date={row.enteredOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getSortingFn("ENTERED_ON", sort, setSort, setFilters),
      },
      {
        id: "default-enteredBy",
        Header: "Entered by",
        accessor: (row) => <GetPersons persons={row.enteredBy} createLinks={false} />,
        width: 200,
        align: "left",
        ...getSortingFn("ENTERED_BY", sort, setSort, setFilters),
      },
      {
        id: "default-modifiedOn",
        Header: "Last modified on",
        accessor: (row) => <DateTimeRenderer date={row.modifiedOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getSortingFn("MODIFIED_ON", sort, setSort, setFilters),
      },
      {
        id: "default-modifiedBy",
        Header: "Last modified by",
        accessor: (row) => <GetPersons persons={row.modifiedBy} createLinks={false} />,
        width: 200,
        align: "left",
        ...getSortingFn("MODIFIED_BY", sort, setSort, setFilters),
      },
      {
        id: "default-projects",
        Header: "Projects",
        accessor: (row) => <TableArrayRenderer values={row.projects}>{(value) => value.name}</TableArrayRenderer>,
        width: 200,
        align: "left",
      },
    ] as GenericVirtualizedTableCells<Dataset>;
  }, [sort]);

  const { data: ids } = useIdsOnly<Dataset["id"], DatasetFilters>("datasets", filters);

  const handleDownloadAll = useCallback(async () => {
    if (filters) {
      try {
        // const ids = await api.getDatasetsIds(filterObj);
        await getDatasetsZip(api, { ids: !!selection.size ? Array.from(selection) : ids?.results });
      } catch (e) {
        console.error("Error: ", e);
        showtoast("error", "Sorry, an error occured while processing your request");
      }
    }
  }, [api, filters, ids?.results, selection]);

  const linkTo = useCallback(
    (row: Dataset) => route(`/shared/${api.getAuthentication().value}/${row.id}`),
    [api, route]
  );
  const onRowClick = useCallback(
    (row: Dataset) => history.push(route(`/shared/${api.getAuthentication().value}/${row.id}`)),
    [api, history, route]
  );

  let { sessionId }: { group: string; sessionId?: string | undefined } = useParams();
  const {
    data: pubInfo,
    status,
    fetchStatus,
    error,
  } = usePost<SharedContentPublicModel, SharedContentPublicModelRequest>(
    `shared_content/public/${sessionId}`,
    { password: api.getAuthentication().password },
    { enabled: !!sessionId }
  );

  const _filters = useMemo(() => ({ ...filters, includeParsingState: true }), [filters]);

  return (
    <LoadingWrapper status={status} fetchStatus={fetchStatus} error={error}>
      {pubInfo && (!pubInfo.requiresPassword || pubInfo?.suppliedPasswordCorrect) ? (
        <TableView>
          <TableView.Head>
            <TableView.Head.Label>
              <div className="flex col-nowrap" style={{ overflow: "hidden" }}>
                <div className="flex row-nowrap align-center gap-5">
                  {pubInfo.name} {typeof resultsCount === "number" && <span className="badge">{resultsCount}</span>}
                </div>
                <span
                  style={{
                    color: "var(--gray-400)",
                    fontSize: "0.75em",
                    fontWeight: "normal",
                    height: "min-content",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                >
                  {pubInfo.publicNotes}
                </span>
              </div>
            </TableView.Head.Label>
            <TableView.Head.Controls>
              {pubInfo.allowContentDownload && (
                <button
                  className="btn btn-primary"
                  onClick={handleDownloadAll}
                  disabled={!pubInfo.allowContentDownload}
                >
                  <LucideIcon name="download" /> Download {`${!!selection.size ? "selected" : "all"}`}
                </button>
              )}
            </TableView.Head.Controls>
          </TableView.Head>
          <TableView.Body>
            <TableView.Body.Sidebar>
              <SharedBundlesFilterBar setFilters={setFilters} />
            </TableView.Body.Sidebar>
            <TableView.Body.Content>
              <EntityTable>
                <EntityTable.Controls>
                  <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" />
                </EntityTable.Controls>
                <EntityTable.Body<Dataset, DatasetFilters>
                  entityConstants={datasetsConstants}
                  filters={_filters}
                  columns={columns}
                  columnSelect
                  defaultColumnSettings={columnSetting}
                  setResultsCount={onCountChange}
                  onSelectionChange={onSelectionChange}
                  linkTo={linkTo}
                  onRowClick={onRowClick}
                  disableCheckboxes={!pubInfo.allowContentDownload}
                />
              </EntityTable>
            </TableView.Body.Content>
          </TableView.Body>
        </TableView>
      ) : (
        <Alert type="danger" message="Resource not found or unauthorized" fit centered />
      )}
    </LoadingWrapper>
  );
};
